import {
  Stack, Text, Box, Table, Badge, Group, Button, Modal, TextInput,
  Select, Textarea, Skeleton, ActionIcon, Tooltip, SimpleGrid, ThemeIcon, Alert,
} from '@mantine/core'
import { WidgetCard, EmptyState } from '@/components/ui'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconEdit, IconTrash, IconShield, IconSearch, IconUsersGroup, IconCrown, IconSearchOff, IconInfoCircle, IconShieldLock } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { rolesApi, type Role } from '@/api/roles'
import { RoleGatesPanel } from '@/components/admin/RoleGatesPanel'
import { formatApiError } from '@/api/errors'

const COLOR_OPTIONS = [
  'red', 'orange', 'yellow', 'green', 'cyan', 'blue', 'violet', 'grape', 'pink', 'gray',
] as const

const colorSelectData = COLOR_OPTIONS.map((c) => ({
  value: c,
  label: c.charAt(0).toUpperCase() + c.slice(1),
}))

function ColorCircle({ color }: { color: string }) {
  return (
    <Box
      style={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: `var(--mantine-color-${color}-6)`,
        flexShrink: 0,
      }}
    />
  )
}

function ColorSelectOption({ value, label }: { value: string; label: string }) {
  return (
    <Group gap="xs">
      <ColorCircle color={value} />
      <Text size="sm">{label}</Text>
    </Group>
  )
}

const emptyForm = { code: '', name: '', description: '', color: 'blue' }

function EditRoleModalContent({
  form,
  setField,
  updateMutation,
  onClose,
}: {
  form: typeof emptyForm
  setField: (field: string, value: string) => void
  updateMutation: ReturnType<typeof useMutation<any, any, void, unknown>>
  onClose: () => void
}) {
  return (
    <Stack gap="md">
      <TextInput label="Código" value={form.code} disabled />
      <TextInput
        label="Nome"
        placeholder="Nome da role"
        value={form.name}
        onChange={(e) => setField('name', e.target.value)}
        required
      />
      <Textarea
        label="Descrição"
        placeholder="Descreva o propósito desta role"
        value={form.description}
        onChange={(e) => setField('description', e.target.value)}
        minRows={2}
      />
      <Select
        label="Cor"
        data={colorSelectData}
        value={form.color}
        onChange={(v) => setField('color', v ?? 'blue')}
        renderOption={({ option }) => <ColorSelectOption value={option.value} label={option.label} />}
        leftSection={<ColorCircle color={form.color} />}
      />
      <Group justify="flex-end" mt="sm">
        <Button variant="default" onClick={onClose}>Cancelar</Button>
        <Button
          loading={updateMutation.isPending}
          onClick={() => updateMutation.mutate()}
          disabled={!form.name}
        >
          Salvar
        </Button>
      </Group>
    </Stack>
  )
}

export function Roles() {
  const qc = useQueryClient()
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [editRole, setEditRole] = useState<Role | null>(null)
  const [deleteRole, setDeleteRole] = useState<Role | null>(null)
  const [gatesRole, setGatesRole] = useState<Role | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const [form, setForm] = useState(emptyForm)
  const setField = (field: string, value: string) => setForm((prev) => ({ ...prev, [field]: value }))

  const { data: roles = [], isLoading } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => rolesApi.listRoles(),
  })

  const totalUsers = useMemo(() => roles.reduce((sum, r) => sum + (r.user_count ?? 0), 0), [roles])
  const mostUsed = useMemo(() => {
    if (!roles.length) return '—'
    const top = roles.reduce((a, b) => ((a.user_count ?? 0) >= (b.user_count ?? 0) ? a : b))
    return top.user_count > 0 ? (top.name || top.code) : '—'
  }, [roles])

  const filteredRoles = useMemo(() => {
    if (!searchQuery.trim()) return roles
    const q = searchQuery.toLowerCase()
    return roles.filter(
      (r) => r.name?.toLowerCase().includes(q) || r.code.toLowerCase().includes(q),
    )
  }, [roles, searchQuery])

  // --- Mutations ---

  const createMutation = useMutation({
    mutationFn: () => rolesApi.createRole(form.code, form.name, form.description, form.color),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] })
      notifications.show({ color: 'teal', title: 'Role criada', message: `Role "${form.name || form.code}" criada com sucesso.` })
      closeCreate()
      setForm(emptyForm)
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Erro ao criar role.') })
    },
  })

  const updateMutation = useMutation({
    mutationFn: () =>
      rolesApi.updateRole(editRole!.id, { name: form.name, description: form.description, color: form.color }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] })
      notifications.show({ color: 'teal', title: 'Role atualizada', message: `Role "${form.name || editRole?.code}" atualizada.` })
      setEditRole(null)
      setForm(emptyForm)
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Erro ao atualizar role.') })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => rolesApi.deleteRole(deleteRole!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] })
      notifications.show({ color: 'teal', title: 'Role removida', message: `Role "${deleteRole?.name || deleteRole?.code}" removida.` })
      setDeleteRole(null)
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Erro ao remover role.') })
    },
  })

  const openEditModal = (r: Role) => {
    setEditRole(r)
    setForm({ code: r.code, name: r.name, description: r.description, color: r.color || 'blue' })
  }

  const openCreateModal = () => {
    setForm(emptyForm)
    openCreate()
  }

  return (
    <Stack gap="lg">
      <Alert color="inovareTeal" variant="light" icon={<IconInfoCircle size={16} />}>
        <Text size="sm" fw={600} mb={4}>Função não concede permissão</Text>
        <Text size="xs">
          Quem concede permissão é o <b>Perfil</b> (em <b>Admin → Permissões</b>). Função
          é um marcador que regras específicas do código consultam para abrir ou fechar
          acesso — e <b>quais</b> regras não se escreve aqui: abra{' '}
          <IconShieldLock size={13} style={{ verticalAlign: -2 }} /> na linha da função
          para ver os pontos derivados do código em execução.
        </Text>
        <Text size="xs" mt={6} c="dimmed">
          Atribuir e remover função de um usuário é feito no drawer do usuário, em
          Admin → Usuários.
        </Text>
      </Alert>

      {/* KPI cards */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <WidgetCard accent="primary" p="md">
          <Group gap="md">
            <ThemeIcon size={44} radius="md" variant="light" color="inovareTeal">
              <IconShield size={22} />
            </ThemeIcon>
            <Box>
              <Text size="xl" fw={700} c="var(--ai-text-primary)" style={{ lineHeight: 1 }}>
                {isLoading ? '—' : roles.length}
              </Text>
              <Text size="xs" c="var(--ai-text-muted)" mt={2}>Total de roles</Text>
            </Box>
          </Group>
        </WidgetCard>

        <WidgetCard accent="none" p="md">
          <Group gap="md">
            <ThemeIcon size={44} radius="md" variant="light" color="teal">
              <IconUsersGroup size={22} />
            </ThemeIcon>
            <Box>
              <Text size="xl" fw={700} c="var(--ai-text-primary)" style={{ lineHeight: 1 }}>
                {isLoading ? '—' : totalUsers}
              </Text>
              <Text size="xs" c="var(--ai-text-muted)" mt={2}>Usuários com roles</Text>
            </Box>
          </Group>
        </WidgetCard>

        <WidgetCard accent="none" p="md">
          <Group gap="md">
            <ThemeIcon size={44} radius="md" variant="light" color="violet">
              <IconCrown size={22} />
            </ThemeIcon>
            <Box>
              <Text size="xl" fw={700} c="var(--ai-text-primary)" style={{ lineHeight: 1 }}>
                {isLoading ? '—' : mostUsed}
              </Text>
              <Text size="xs" c="var(--ai-text-muted)" mt={2}>Role mais utilizada</Text>
            </Box>
          </Group>
        </WidgetCard>
      </SimpleGrid>

      {/* Toolbar */}
      <Group justify="space-between">
        <TextInput
          placeholder="Buscar por nome ou código..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          style={{ width: 320 }}
          size="sm"
        />
        <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
          Nova Role
        </Button>
      </Group>

      {/* Roles table */}
      <WidgetCard accent="none" p={0}>
        {isLoading ? (
          <Box p="xl"><Skeleton height={300} /></Box>
        ) : filteredRoles.length === 0 ? (
          <EmptyState
            bare
            size="md"
            accent="info"
            icon={searchQuery ? IconSearchOff : IconShield}
            title={searchQuery ? 'Nenhuma função encontrada' : 'Nenhuma função cadastrada'}
            description={searchQuery
              ? 'Nenhuma função corresponde à busca atual. Ajuste o termo ou limpe o filtro.'
              : 'Cadastre a primeira função para definir conjuntos reutilizáveis de permissões.'}
          />
        ) : (
          <Table.ScrollContainer minWidth={700}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 50 }}>Cor</Table.Th>
                  <Table.Th>Código</Table.Th>
                  <Table.Th>Nome</Table.Th>
                  <Table.Th>Descrição</Table.Th>
                  <Table.Th style={{ width: 90 }}>Usuários</Table.Th>
                  <Table.Th style={{ width: 100 }}>Ações</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredRoles.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Badge size="xs" variant="filled" color={r.color || 'blue'} style={{ width: 28, padding: 0 }}>
                        &nbsp;
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500} ff="monospace">{r.code}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="var(--ai-text-primary)">{r.name || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="var(--ai-text-muted)" lineClamp={1}>{r.description || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={r.user_count > 0 ? 'blue' : 'gray'}>
                        {r.user_count ?? 0}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Tooltip label="Onde esta função gateia">
                          <ActionIcon variant="subtle" color="inovareTeal" size="sm" onClick={() => setGatesRole(r)}>
                            <IconShieldLock size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Editar role">
                          <ActionIcon variant="subtle" color="inovareTeal" size="sm" onClick={() => openEditModal(r)}>
                            <IconEdit size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={r.user_count > 0 ? 'Remova os usuários antes de excluir' : 'Excluir role'}>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            disabled={r.user_count > 0}
                            onClick={() => setDeleteRole(r)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </WidgetCard>

      {/* Create modal */}
      <Modal opened={createOpened} onClose={closeCreate} title="Nova Role" centered>
        <Stack gap="md">
          <TextInput
            label="Código"
            description="Slug único (ex: devops, viewer)"
            placeholder="minha-role"
            value={form.code}
            onChange={(e) => setField('code', e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ''))}
            required
          />
          <TextInput
            label="Nome"
            placeholder="Nome da role"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            required
          />
          <Textarea
            label="Descrição"
            placeholder="Descreva o propósito desta role"
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            minRows={2}
          />
          <Select
            label="Cor"
            data={colorSelectData}
            value={form.color}
            onChange={(v) => setField('color', v ?? 'blue')}
            renderOption={({ option }) => <ColorSelectOption value={option.value} label={option.label} />}
            leftSection={<ColorCircle color={form.color} />}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeCreate}>Cancelar</Button>
            <Button
              loading={createMutation.isPending}
              onClick={() => createMutation.mutate()}
              disabled={!form.code || !form.name}
            >
              Criar
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit modal */}
      <Modal opened={editRole !== null} onClose={() => setEditRole(null)} title={`Editar Role — ${editRole?.code ?? ''}`} centered>
        <EditRoleModalContent
          form={form}
          setField={setField}
          updateMutation={updateMutation}
          onClose={() => setEditRole(null)}
        />
      </Modal>

      {/* Onde esta funcao gateia — somente leitura */}
      <Modal
        opened={gatesRole !== null}
        onClose={() => setGatesRole(null)}
        title={`Onde esta função gateia — ${gatesRole?.code ?? ''}`}
        centered
        size="lg"
      >
        {gatesRole && <RoleGatesPanel roleCode={gatesRole.code} />}
      </Modal>

      {/* Delete confirmation modal */}
      <Modal opened={deleteRole !== null} onClose={() => setDeleteRole(null)} title="Confirmar exclusão" centered size="sm">
        <Stack gap="md">
          <Text size="sm">
            Tem certeza que deseja excluir a role <Text span fw={700}>"{deleteRole?.name || deleteRole?.code}"</Text>?
            Esta ação não pode ser desfeita.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteRole(null)}>Cancelar</Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              Excluir
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
